import { Service } from '@genial/services';
import {
  CoeSuitabilityOrderValidationRequest,
  EquitySuitabilityValidationRequest,
  FixedIncomeSuitabilityOrderValidationRequest,
  FundSuitabilityValidationRequest,
  TreasurySuitabilityOrderValidationRequest,
} from '@genial/services/dist/models';

import { screenName } from '../order/constants';
import { AssetSelectedTypeMap } from '../product/types';

export type SuitabilityResponseAble = {
  ableToInvest: true;
};

export type SuitabilityResponseUnableModal = {
  ableToInvest: false;
  failureType: 'modal';
  maxSuitabilityAmount: number;
  riskLevel: number;
  portfolioRisk: number;
};

export type SuitabilityResponseUnableToast = {
  ableToInvest: false;
  failureType: 'toast';
  toastTitle: string;
  toastMessage: string;
  maxSuitabilityAmount: number;
  riskLevel: number;
  portfolioRisk: number;
};

export type SuitabilityResponseUnable =
  | SuitabilityResponseUnableModal
  | SuitabilityResponseUnableToast;

export type SuitabilityResponse = SuitabilityResponseAble | SuitabilityResponseUnable;

export enum SuitabilityExternalId {
  Enquadrado = 0,
  Desenquadrado = 1,
  EnquadradoComTermo = 2,
  SuitabilityVencido = 3,
}

const SUITABILITY_TOAST_FALLBACK =
  'Não foi possível validar o perfil do cliente. Por favor, tente novamente mais tarde.';
const SUITABILITY_TOAST_DEFAULT_TITLE = 'Houve um erro ao processar sua solicitação.';
const SUITABILITY_TOAST_TITLE_BY_EXTERNAL_ID: Partial<Record<SuitabilityExternalId, string>> = {
  [SuitabilityExternalId.SuitabilityVencido]: 'Suitability vencido',
};

// Mesma cópia que o BFF devolve em customer_suitability_non_compliance. O serviço de
// suitability não passa por aquele resource, então repetimos o texto aqui. Os números
// da carteira vêm da própria resposta, que o modal e o toast continuam expondo.
const buildMismatchedProfileToast = (
  response: SuitabilityResponseUnable
): SuitabilityResponseUnableToast => ({
  ableToInvest: false,
  failureType: 'toast',
  toastTitle: 'Perfil desenquadrado',
  toastMessage:
    'O perfil do cliente não está adequado com o produto selecionado. Por favor, selecione outro cliente ou produto para prosseguir.',
  maxSuitabilityAmount: response.maxSuitabilityAmount,
  riskLevel: response.riskLevel,
  portfolioRisk: response.portfolioRisk,
});

// errorIds que o BFF mapeia para erro de desenquadramento numa ordem de COE
// (CoeOrderSuitabilityNonComplianceError e CustomerSuitabilityNonComplianceError).
const SUITABILITY_NON_COMPLIANCE_ERROR_IDS = [4, 9];

const RESTRICTED_SUITABILITY_PROFILES = ['CONSERVADOR', 'MODERADO'];

type SuitabilityOrderErrorFields = {
  externalMessage?: string | null;
  internalMessage?: string | null;
};

const mapSuitabilityValidationApiResponse = (data: {
  maxSuitabilityAmount: number;
  isValid: boolean;
  externalId: number;
  riskLevel: number;
  portfolioRisk: number;
  orderError: SuitabilityOrderErrorFields;
}): SuitabilityResponse => {
  if (data.isValid) {
    return { ableToInvest: true };
  }

  if (data.externalId === SuitabilityExternalId.Desenquadrado) {
    return {
      ableToInvest: false,
      failureType: 'modal',
      maxSuitabilityAmount: data.maxSuitabilityAmount,
      riskLevel: data.riskLevel,
      portfolioRisk: data.portfolioRisk,
    };
  }

  const toastMessage =
    data.orderError?.externalMessage?.trim() ||
    data.orderError?.internalMessage?.trim() ||
    SUITABILITY_TOAST_FALLBACK;
  const toastTitle =
    SUITABILITY_TOAST_TITLE_BY_EXTERNAL_ID[data.externalId as SuitabilityExternalId] ??
    SUITABILITY_TOAST_DEFAULT_TITLE;

  return {
    ableToInvest: false,
    failureType: 'toast',
    toastTitle,
    toastMessage,
    maxSuitabilityAmount: data.maxSuitabilityAmount,
    riskLevel: data.riskLevel,
    portfolioRisk: data.portfolioRisk,
  };
};

export type SuitabilityCustomPropsMap = {
  fixedIncome: FixedIncomeSuitabilityOrderValidationRequest;
  funds: FundSuitabilityValidationRequest;
  directTreasury: TreasurySuitabilityOrderValidationRequest;
  equity: Omit<EquitySuitabilityValidationRequest, 'accountNumber'>;
  coes: CoeSuitabilityOrderValidationRequest;
} & {
  [K in Exclude<
    keyof AssetSelectedTypeMap,
    'fixedIncome' | 'funds' | 'directTreasury' | 'equity' | 'coes'
  >]: Record<string, never>[];
};

export interface CheckSuitabilityPerProductInput<T extends keyof AssetSelectedTypeMap> {
  accountNumber: number;
  suitabilityProfileName?: string | null;
  productType: T;
  customProps: SuitabilityCustomPropsMap[T];
}

type SuitabilityValidatorMap = {
  [K in keyof AssetSelectedTypeMap]: (
    props: CheckSuitabilityPerProductInput<K>
  ) => Promise<SuitabilityResponse>;
};

const checkSuitabilityFixedIncome = async ({
  accountNumber,
  customProps,
}: CheckSuitabilityPerProductInput<'fixedIncome'>): Promise<SuitabilityResponse> => {
  const { data } = await Service.FixedIncome.V1.postValidateSuitability({
    screenName,
    fixedIncomeSuitabilityOrderValidationRequest: {
      ...customProps,
      accountNumber,
    },
  });

  return mapSuitabilityValidationApiResponse(data);
};

const checkSuitabilityFunds = async ({
  accountNumber,
  customProps,
}: CheckSuitabilityPerProductInput<'funds'>): Promise<SuitabilityResponse> => {
  const { data } = await Service.Fund.V1.postValidateSuitability({
    screenName,
    fundSuitabilityValidationRequest: {
      ...customProps,
      accountNumber,
    },
  });

  return mapSuitabilityValidationApiResponse(data);
};

const checkSuitabilityDirectTreasury = async ({
  accountNumber,
  customProps,
}: CheckSuitabilityPerProductInput<'directTreasury'>): Promise<SuitabilityResponse> => {
  const { data } = await Service.Treasury.V1.postValidateSuitability({
    screenName,
    treasurySuitabilityOrderValidationRequest: {
      ...customProps,
      accountNumber,
    },
  });

  return mapSuitabilityValidationApiResponse(data);
};

const checkSuitabilityEquity = async ({
  accountNumber,
  customProps,
}: CheckSuitabilityPerProductInput<'equity'>): Promise<SuitabilityResponse> => {
  const { data } = await Service.Equities.V1.postValidateSuitability({
    screenName,
    equitySuitabilityValidationRequest: {
      ...customProps,
      accountNumber,
    },
  });

  return mapSuitabilityValidationApiResponse(data);
};

const checkSuitabilityCoes = async ({
  accountNumber,
  suitabilityProfileName,
  customProps,
}: CheckSuitabilityPerProductInput<'coes'>): Promise<SuitabilityResponse> => {
  const { data } = await Service.COES.V1.postValidateSuitability({
    screenName,
    coeSuitabilityOrderValidationRequest: {
      ...customProps,
      accountNumber,
    },
  });

  const response = mapSuitabilityValidationApiResponse(data);

  if (response.ableToInvest) {
    return response;
  }

  const hasRestrictedProfile = RESTRICTED_SUITABILITY_PROFILES.includes(
    suitabilityProfileName?.trim().toUpperCase() ?? ''
  );

  const isNonCompliance =
    data.externalId === SuitabilityExternalId.Desenquadrado ||
    SUITABILITY_NON_COMPLIANCE_ERROR_IDS.includes(data.orderError?.errorId);

  if (hasRestrictedProfile && isNonCompliance) {
    return buildMismatchedProfileToast(response);
  }

  return response;
};

const suitabilityValidatorPerProduct: Partial<SuitabilityValidatorMap> = {
  fixedIncome: checkSuitabilityFixedIncome,
  funds: checkSuitabilityFunds,
  directTreasury: checkSuitabilityDirectTreasury,
  equity: checkSuitabilityEquity,
  coes: checkSuitabilityCoes,
};

export const checkSuitabilityPerProduct = <T extends keyof AssetSelectedTypeMap>(
  params: CheckSuitabilityPerProductInput<T>
): Promise<SuitabilityResponse> => {
  const validator = suitabilityValidatorPerProduct[params.productType] as
    | SuitabilityValidatorMap[T]
    | undefined;

  if (!validator) {
    return Promise.resolve({ ableToInvest: true });
  }

  return validator(params);
};
